import { useMemo, useState } from "react";
import type { RepairCapacityDay } from "../types";
import { t, type Locale } from "../i18n";

type RepairDayCalendarProps = {
  locale: Locale;
  days: RepairCapacityDay[];
  selectedDate: string | null;
  dropoffChoice: string | null;
  onSelectDate: (dateKey: string) => void;
  onSelectDropoff: (choice: string) => void;
};

function parseKey(key: string): Date | null {
  const [year, month, day] = key.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function toKey(date: Date): string {
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${m}-${d}`;
}

function formatDateKey(dateKey: string, locale: Locale): string {
  const date = parseKey(dateKey);
  if (!date) return dateKey;
  const localeTag =
    locale === "de" ? "de-DE" : locale === "es" ? "es-ES" : locale === "fr" ? "fr-FR" : "en-GB";
  return date.toLocaleDateString(localeTag, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

function fill(template: string, vars: Record<string, string>): string {
  return Object.entries(vars).reduce(
    (s, [key, value]) => s.replaceAll(`{${key}}`, value),
    template
  );
}

export function RepairDayCalendar({
  locale,
  days,
  selectedDate,
  dropoffChoice,
  onSelectDate,
  onSelectDropoff,
}: RepairDayCalendarProps) {
  const availableKeys = useMemo(
    () => new Set(days.filter((d) => d.available).map((d) => d.date)),
    [days]
  );
  const availableDates = useMemo(
    () =>
      days
        .filter((d) => d.available)
        .map((d) => parseKey(d.date))
        .filter((d): d is Date => d != null)
        .sort((a, b) => a.getTime() - b.getTime()),
    [days]
  );
  const months = useMemo(() => {
    const seen = new Set<string>();
    const list: Date[] = [];
    for (const date of availableDates) {
      const key = `${date.getFullYear()}-${date.getMonth()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      list.push(new Date(date.getFullYear(), date.getMonth(), 1));
    }
    return list;
  }, [availableDates]);

  const [month, setMonth] = useState(() => {
    const selected = selectedDate ? parseKey(selectedDate) : null;
    if (selected) return new Date(selected.getFullYear(), selected.getMonth(), 1);
    if (availableDates[0]) {
      return new Date(availableDates[0].getFullYear(), availableDates[0].getMonth(), 1);
    }
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const monthIndex = months.findIndex(
    (m) => m.getFullYear() === month.getFullYear() && m.getMonth() === month.getMonth()
  );
  const canPrev = monthIndex > 0;
  const canNext = monthIndex >= 0 && monthIndex < months.length - 1;
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const lead = (first.getDay() + 6) % 7; // Monday-first
  const cellCount = lead + daysInMonth;
  const todayKey = toKey(new Date());
  const localeTag =
    locale === "de" ? "de-DE" : locale === "es" ? "es-ES" : locale === "fr" ? "fr-FR" : "en-GB";
  const weekdayLabels = Array.from({ length: 7 }, (_, i) =>
    new Date(2024, 0, 1 + i).toLocaleDateString(localeTag, { weekday: "short" })
  );
  const selectedDay = days.find((d) => d.date === selectedDate);

  return (
    <div className="rr-calendar">
      <div className="rr-calendar-nav">
        <button
          type="button"
          className="chip"
          disabled={!canPrev}
          onClick={() => canPrev && setMonth(months[monthIndex - 1])}
        >
          ‹
        </button>
        <strong>
          {first.toLocaleDateString(localeTag, { month: "long", year: "numeric" })}
        </strong>
        <button
          type="button"
          className="chip"
          disabled={!canNext}
          onClick={() => canNext && setMonth(months[monthIndex + 1])}
        >
          ›
        </button>
      </div>
      <div className="rr-calendar-weekdays">
        {weekdayLabels.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
      <div className="rr-calendar-grid">
        {Array.from({ length: cellCount }, (_, index) => {
          const dayNum = index - lead + 1;
          if (dayNum < 1 || dayNum > daysInMonth) {
            return <span key={`empty-${index}`} />;
          }
          const date = new Date(month.getFullYear(), month.getMonth(), dayNum);
          const key = toKey(date);
          const available = availableKeys.has(key);
          const selected = selectedDate === key;
          const isToday = key === todayKey;
          return (
            <button
              key={key}
              type="button"
              disabled={!available}
              className={`rr-calendar-cell${selected ? " selected" : ""}${
                isToday ? " today" : ""
              }`}
              onClick={() => onSelectDate(key)}
            >
              {dayNum}
            </button>
          );
        })}
      </div>
      {selectedDay && selectedDay.dropoffOptions.length > 0 && (
        <>
          <h3 style={{ marginTop: 20 }}>{t(locale, "rrDropoffChoice")}</h3>
          <div className="rr-dropoff-cards">
            {selectedDay.dropoffOptions.map((opt) => (
              <button
                key={opt.choice}
                type="button"
                className={`rr-dropoff-card${dropoffChoice === opt.choice ? " selected" : ""}`}
                onClick={() => onSelectDropoff(opt.choice)}
              >
                {fill(
                  t(
                    locale,
                    opt.choice === "dayBefore"
                      ? "rrDropoffDayBeforeChoice"
                      : "rrDropoffSameDayChoice"
                  ),
                  {
                    date: formatDateKey(opt.date, locale),
                    from: opt.from,
                    to: opt.to,
                  }
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export { formatDateKey };
